import { Box, Button, Typography, useTheme } from '@mui/material';
import type { Workout, WorkoutStep } from '@shared/types';
import React, { useState } from 'react';
import {
  Area,
  AreaChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface WorkoutGraphProps {
  workout: Workout;
  /** User's FTP in watts (for indoor cycling power display) */
  userFTP?: number;
}

const WorkoutGraph: React.FC<WorkoutGraphProps> = ({ workout, userFTP }) => {
  const theme = useTheme();
  const [xAxisUnit, setXAxisUnit] = useState<'time' | 'distance'>('time');

  // Determine if this is a cycling workout
  const isCycling = workout.sportType === 'cycling';

  // Helper to check if steps contain FTP-based structure (indicating indoor cycling)
  const hasFtpBasedSteps = (steps: WorkoutStep[]): boolean => {
    return steps.some(step => {
      if (step.ftpPercent !== undefined) return true;
      if (step.ramp?.startFtpPercent !== undefined) return true;
      if (step.steps && step.steps.length > 0) return hasFtpBasedSteps(step.steps);
      return false;
    });
  };

  // Check if this is an indoor cycling workout using environment property OR FTP-based steps
  const isIndoorCycling =
    isCycling && (workout.environment === 'indoor' || hasFtpBasedSteps(workout.steps));

  // Map zone to FTP percentage midpoint (for indoor cycling)
  const zoneToFtpPercent = (zone: number): number => {
    switch (zone) {
      case 1:
        return 65; // 55-74% FTP - midpoint
      case 2:
        return 82; // 75-89% FTP - midpoint
      case 3:
        return 92; // 90-94% FTP - midpoint
      case 4:
        return 100; // 95-105% FTP - midpoint
      case 5:
        return 113; // 106-120% FTP - midpoint
      default:
        return 75;
    }
  };

  // Convert FTP percentage to watts
  const ftpPercentToWatts = (ftpPercent: number): number => {
    const ftp = userFTP || 200; // Default 200W if not provided
    return Math.round((ftpPercent / 100) * ftp);
  };

  // Get color based on FTP percentage or zone
  const getIntensityColor = (ftpPercent: number) => {
    if (ftpPercent < 56) return theme.palette.grey[400]; // Recovery
    if (ftpPercent < 76) return theme.palette.info.main; // Endurance (Z1)
    if (ftpPercent < 90) return theme.palette.success.main; // Tempo (Z2)
    if (ftpPercent < 95) return theme.palette.warning.main; // Sweet Spot (Z3)
    if (ftpPercent < 106) return theme.palette.error.main; // Threshold (Z4)
    return theme.palette.error.dark; // VO2max+ (Z5+)
  };

  const getZoneColor = (zone: number | undefined) => {
    switch (zone) {
      case 1:
        return theme.palette.info.main;
      case 2:
        return theme.palette.success.main;
      case 3:
        return theme.palette.warning.main;
      case 4:
        return theme.palette.error.main;
      case 5:
        return theme.palette.error.dark;
      default:
        return theme.palette.grey[500];
    }
  };

  const processStepsForChart = (steps: WorkoutStep[]) => {
    const data: any[] = [];
    let cumulativeTime = 0;
    let cumulativeDistance = 0;

    const flatten = (stepList: WorkoutStep[]) => {
      stepList.forEach(originalStep => {
        const repeatCount = originalStep.repeat || 1;
        for (let i = 0; i < repeatCount; i++) {
          // Create a mutable copy for processing
          const step = { ...originalStep };

          // If the step has sub-steps, recurse into them.
          if (step.steps && step.steps.length > 0) {
            flatten(step.steps);
            continue; // Continue to next iteration of the loop
          }

          // Handle cycling workouts (speed in km/h) vs running (pace in sec/km)
          if (isCycling) {
            // For indoor cycling, convert zone to watts
            if (isIndoorCycling) {
              // Handle ramp steps with FTP% progression (e.g., 55% → 85% FTP)
              if (
                step.duration &&
                step.ramp?.startFtpPercent !== undefined &&
                step.ramp?.endFtpPercent !== undefined
              ) {
                const startFtp = step.ramp.startFtpPercent;
                const endFtp = step.ramp.endFtpPercent;
                const startWatts = ftpPercentToWatts(startFtp);
                const endWatts = ftpPercentToWatts(endFtp);

                // Create start point
                const startTime = cumulativeTime;

                data.push({
                  time: startTime,
                  watts: startWatts,
                  ftpPercent: startFtp,
                  isRampStart: true,
                });

                // Create end point (diagonal line)
                cumulativeTime += step.duration;

                data.push({
                  time: cumulativeTime,
                  watts: endWatts,
                  ftpPercent: endFtp,
                  isRampEnd: true,
                });
              }
              // Handle ramp steps with zone progression (legacy format)
              else if (step.duration && step.ramp?.startZone && step.ramp?.endZone) {
                const startFtp = zoneToFtpPercent(step.ramp.startZone);
                const endFtp = zoneToFtpPercent(step.ramp.endZone);
                const startWatts = ftpPercentToWatts(startFtp);
                const endWatts = ftpPercentToWatts(endFtp);

                // Create start point
                const startTime = cumulativeTime;

                data.push({
                  time: startTime,
                  watts: startWatts,
                  ftpPercent: startFtp,
                  zone: step.ramp.startZone,
                  isRampStart: true,
                });

                // Create end point (diagonal line)
                cumulativeTime += step.duration;

                data.push({
                  time: cumulativeTime,
                  watts: endWatts,
                  ftpPercent: endFtp,
                  zone: step.ramp.endZone,
                  isRampEnd: true,
                });
              }
              // Handle FTP percentage based steps (single value or range)
              else if (step.duration && step.ftpPercent !== undefined) {
                // Support both single value (number) and range ({ min, max })
                const ftpValue =
                  typeof step.ftpPercent === 'number'
                    ? step.ftpPercent
                    : (step.ftpPercent.min + step.ftpPercent.max) / 2;
                const watts = ftpPercentToWatts(ftpValue);
                const startTime = cumulativeTime;
                cumulativeTime += step.duration;
                const endTime = cumulativeTime;

                const wattsData: Record<string, unknown> = {
                  time: startTime,
                  watts: watts,
                  ftpPercent: ftpValue,
                };

                // Add min/max only if it's a range
                if (typeof step.ftpPercent === 'object') {
                  wattsData.ftpMin = step.ftpPercent.min;
                  wattsData.ftpMax = step.ftpPercent.max;
                }

                data.push(wattsData);
                data.push({ ...wattsData, time: endTime });
              }
              // Handle zone-based steps (convert zone to FTP%)
              else if (step.duration && step.zone) {
                const ftpPercent = zoneToFtpPercent(step.zone);
                const watts = ftpPercentToWatts(ftpPercent);
                const startTime = cumulativeTime;
                cumulativeTime += step.duration;
                const endTime = cumulativeTime;

                const wattsData = {
                  time: startTime,
                  watts: watts,
                  ftpPercent: ftpPercent,
                  zone: step.zone,
                };

                data.push(wattsData);
                data.push({ ...wattsData, time: endTime });
              }
            } else {
              // For outdoor cycling, use speed
              const speedForCalc = step.speed || 25; // Default 25 km/h if not set

              // Calculate duration or distance if not present
              if (!step.duration && step.distance && speedForCalc > 0) {
                step.duration = (step.distance / 1000 / speedForCalc) * 3600; // hours to seconds
              } else if (!step.distance && step.duration && speedForCalc > 0) {
                step.distance = (step.duration / 3600) * speedForCalc * 1000; // to meters
              }

              // Handle ramp steps - create diagonal line showing gradual zone progression
              if (
                step.duration &&
                step.ramp &&
                step.ramp.startZone !== undefined &&
                step.ramp.endZone !== undefined
              ) {
                const startZone = step.ramp.startZone;
                const endZone = step.ramp.endZone;
                const stepDistance = step.distance || (step.duration / 3600) * speedForCalc * 1000;

                // Get speed ranges for start and end zones (use midpoint of zone speed)
                const getZoneSpeed = (zone: number): number => {
                  // Map zone to typical cycling speeds (km/h)
                  const zoneSpeeds: Record<number, number> = {
                    1: 20, // Recovery
                    2: 25, // Endurance
                    3: 30, // Tempo
                    4: 35, // Threshold
                    5: 40, // VO2max
                  };
                  return zoneSpeeds[zone] || 25;
                };

                const startSpeed = getZoneSpeed(startZone);
                const endSpeed = getZoneSpeed(endZone);

                // Create start point
                const startTime = cumulativeTime;
                const startDist = cumulativeDistance;

                data.push({
                  time: startTime,
                  distance: startDist,
                  speed: startSpeed,
                  zone: startZone,
                  isRampStart: true,
                });

                // Create end point (this creates a diagonal line)
                cumulativeTime += step.duration;
                cumulativeDistance += stepDistance;

                data.push({
                  time: cumulativeTime,
                  distance: cumulativeDistance,
                  speed: endSpeed,
                  zone: endZone,
                  isRampEnd: true,
                });
              }
              // Process regular steps if we have the needed data
              else if (step.duration && step.zone) {
                const startTime = cumulativeTime;
                const startDistance = cumulativeDistance;
                cumulativeTime += step.duration;
                // Calculate distance from duration and speed if not set
                const stepDistance = step.distance || (step.duration / 3600) * speedForCalc * 1000;
                cumulativeDistance += stepDistance;
                const endTime = cumulativeTime;
                const endDistance = cumulativeDistance;

                const speedData = {
                  time: startTime,
                  distance: startDistance,
                  speed: speedForCalc,
                  zone: step.zone,
                };

                data.push(speedData);
                data.push({ ...speedData, time: endTime, distance: endDistance });
              }
            }
          } else {
            // Running workout - use pace
            const paceForCalc =
              step.pace || (step.paceLow && step.paceHigh ? (step.paceLow + step.paceHigh) / 2 : 0);

            // Calculate duration or distance if not present
            if (!step.duration && step.distance && paceForCalc > 0) {
              step.duration = paceForCalc * (step.distance / 1000);
            } else if (!step.distance && step.duration && paceForCalc > 0) {
              step.distance = (step.duration / paceForCalc) * 1000;
            }

            // Otherwise, if it's a concrete step with duration, process it.
            if (step.duration && step.distance && (step.pace || (step.paceLow && step.paceHigh))) {
              const startTime = cumulativeTime;
              const startDistance = cumulativeDistance;
              cumulativeTime += step.duration;
              cumulativeDistance += step.distance;
              const endTime = cumulativeTime;
              const endDistance = cumulativeDistance;

              const paceData = {
                time: startTime,
                distance: startDistance,
                pace: step.pace,
                paceLow: step.paceLow,
                paceHigh: step.paceHigh,
                zone: step.zone,
              };

              data.push(paceData);
              data.push({ ...paceData, time: endTime, distance: endDistance });
            }
          }
        }
      });
    };

    flatten(steps);
    return data;
  };

  const chartData = processStepsForChart(workout.steps);

  // Determine Y-axis values based on workout type
  let yValues: number[];
  if (isIndoorCycling) {
    // For indoor cycling: use watts values
    yValues = chartData.map(d => d.watts).filter((w): w is number => w !== undefined && w > 0);
  } else if (isCycling) {
    // For outdoor cycling: use speed values
    yValues = chartData.map(d => d.speed).filter((s): s is number => s !== undefined && s > 0);
  } else {
    // For running: use pace values
    yValues = chartData
      .flatMap(d => [d.pace, d.paceLow, d.paceHigh])
      .filter((p): p is number => p !== undefined && p > 0);
  }

  const minY = yValues.length > 0 ? Math.min(...yValues) : 0;
  const maxY =
    yValues.length > 0
      ? Math.max(...yValues)
      : isIndoorCycling
        ? ftpPercentToWatts(120)
        : isCycling
          ? 40
          : 300;

  const formatPace = (pace: number) => {
    const minutes = Math.floor(pace / 60);
    const seconds = (pace % 60).toFixed(0).padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  const formatSpeed = (speed: number) => {
    return `${speed.toFixed(1)}`;
  };

  const formatWatts = (watts: number) => {
    return `${Math.round(watts)}W`;
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = (time % 60).toFixed(0).padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  const formatDistance = (distance: number) => {
    return `${(distance / 1000).toFixed(2)} km`;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            padding: '10px',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '4px',
          }}
        >
          <Typography variant="body2">{`Time: ${formatTime(label)}`}</Typography>
          {isIndoorCycling ? (
            <>
              <Typography variant="body2">{`Power: ${Math.round(data.watts)}W`}</Typography>
              <Typography variant="body2" color="text.secondary">
                {`(${Math.round(data.ftpPercent)}% FTP)`}
              </Typography>
            </>
          ) : isCycling ? (
            <Typography variant="body2">{`Speed: ${formatSpeed(data.speed)} km/h`}</Typography>
          ) : (
            <Typography variant="body2">{`Pace: ${formatPace(data.pace)}/km`}</Typography>
          )}
          {data.zone && <Typography variant="body2">{`Zone: ${data.zone}`}</Typography>}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box sx={{ mt: 3, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
          Workout Profile
        </Typography>
        {/* Hide x-axis toggle for indoor cycling - always use time */}
        {!isIndoorCycling && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setXAxisUnit(prev => (prev === 'time' ? 'distance' : 'time'))}
            aria-label={`Switch workout graph to show by ${xAxisUnit === 'time' ? 'distance' : 'time'}`}
          >
            {`Show by ${xAxisUnit === 'time' ? 'Distance' : 'Time'}`}
          </Button>
        )}
      </Box>
      <ResponsiveContainer width="100%" height={150}>
        <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
          <XAxis
            dataKey={isIndoorCycling ? 'time' : xAxisUnit}
            type="number"
            domain={['dataMin', 'dataMax']}
            tickFormatter={isIndoorCycling || xAxisUnit === 'time' ? formatTime : formatDistance}
            stroke={theme.palette.text.secondary}
          />
          <YAxis
            dataKey={isIndoorCycling ? 'watts' : isCycling ? 'speed' : 'pace'}
            type="number"
            domain={
              isIndoorCycling
                ? [Math.max(0, minY - 20), maxY + 20]
                : isCycling
                  ? [Math.max(0, minY - 5), maxY + 5]
                  : [minY - 30, maxY + 30]
            }
            reversed={!isCycling && !isIndoorCycling} // Pace is reversed (lower = faster), watts/speed is not
            tickFormatter={isIndoorCycling ? formatWatts : isCycling ? formatSpeed : formatPace}
            stroke={theme.palette.text.secondary}
            label={{
              value: isIndoorCycling ? 'Power (W)' : isCycling ? 'Speed (km/h)' : 'Pace (min/km)',
              angle: -90,
              position: 'insideLeft',
            }}
          />
          <Tooltip content={<CustomTooltip />} />

          {/* Render colored blocks for all segments, including ramp gradient strips */}
          {chartData.map((entry, index) => {
            if (index % 2 === 0 && index < chartData.length - 1) {
              const nextEntry = chartData[index + 1];
              const xKey = isIndoorCycling ? 'time' : xAxisUnit;
              const yBottom = isIndoorCycling
                ? Math.max(0, minY - 20)
                : isCycling
                  ? Math.max(0, minY - 5)
                  : maxY + 30;

              // Handle ramp segments with gradient strips
              if (entry.isRampStart) {
                const startY = isIndoorCycling ? entry.watts : isCycling ? entry.speed : entry.pace;
                const endY = isIndoorCycling
                  ? nextEntry.watts
                  : isCycling
                    ? nextEntry.speed
                    : nextEntry.pace;
                const startX = entry[xKey];
                const endX = nextEntry[xKey];
                const startFtp = entry.ftpPercent || 75;
                const endFtp = nextEntry.ftpPercent || 100;

                // Create multiple strips to approximate gradient
                const numStrips = 10;
                const strips = [];
                for (let i = 0; i < numStrips; i++) {
                  const t1 = i / numStrips;
                  const t2 = (i + 1) / numStrips;
                  const x1 = startX + (endX - startX) * t1;
                  const x2 = startX + (endX - startX) * t2;
                  const y1 = startY + (endY - startY) * t1;
                  const y2 = startY + (endY - startY) * t2;
                  const midT = (t1 + t2) / 2;
                  const midFtp = startFtp + (endFtp - startFtp) * midT;
                  const fillColor = isIndoorCycling
                    ? getIntensityColor(midFtp)
                    : getZoneColor(Math.round(entry.zone + (nextEntry.zone - entry.zone) * midT));

                  strips.push(
                    <ReferenceArea
                      key={`ramp-${index}-strip-${i}`}
                      x1={x1}
                      x2={x2}
                      y1={Math.max(y1, y2)}
                      y2={yBottom}
                      fill={fillColor}
                      fillOpacity={0.3}
                      stroke="transparent"
                    />
                  );
                }
                return strips;
              }

              // Non-ramp segments
              const yValue = isIndoorCycling ? entry.watts : isCycling ? entry.speed : entry.pace;
              const fillColor = isIndoorCycling
                ? getIntensityColor(entry.ftpPercent)
                : getZoneColor(entry.zone);
              return (
                <ReferenceArea
                  key={`ref-${index}`}
                  x1={entry[xKey]}
                  x2={nextEntry[xKey]}
                  y1={yValue}
                  y2={yBottom}
                  fill={fillColor}
                  fillOpacity={0.3}
                  stroke="transparent"
                />
              );
            }
            return null;
          })}

          {/* FTP reference line for indoor cycling */}
          {isIndoorCycling && (
            <ReferenceLine
              y={userFTP || 200}
              stroke={theme.palette.warning.main}
              strokeWidth={2}
              strokeDasharray="5 5"
              label={{
                value: `FTP: ${userFTP || 200}W`,
                position: 'right',
                fill: theme.palette.warning.main,
                fontSize: 12,
                fontWeight: 'bold',
              }}
            />
          )}

          {/* Render the pace/speed/watts line on top */}
          <Area
            type="linear"
            dataKey={isIndoorCycling ? 'watts' : isCycling ? 'speed' : 'pace'}
            stroke={theme.palette.primary.main}
            strokeWidth={2}
            fill="transparent"
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default WorkoutGraph;
